import { ExternalConnection } from '@griply/types';
import Dialog from '@mui/material/Dialog';
import Typography from '@mui/material/Typography';
import { useDeleteExternalConnection } from 'features/external-connections';
import React, { Suspense } from 'react';
import { useTranslation } from 'react-i18next';
import { Trash2 } from 'shared/assets/icons';
import { Button, Colors, Sizes, Variants } from 'shared/components/ui/button';
import { ExternalConnectionIcon } from 'shared/components/ui/external-connection-icon';
import {
  Colors as IconButtonColors,
  IconButton,
  Sizes as IconButtonSizes,
} from 'shared/components/ui/icon-button';
import { useOpenMenu } from 'shared/hooks/use-open-menu';

import {
  CalendarConnectionList,
  LoadingCalendarConnectionList,
} from './calendar-connection-list';
import * as Styled from './external-calendar-connection.style';

export type ExternalCalendarProps = {
  connection: ExternalConnection;
};

export const ExternalCalendarConnection: React.FC<ExternalCalendarProps> = ({
  connection,
}) => {
  const { t } = useTranslation();
  const {
    menuOpen: deleteDialogOpen,
    openMenu: openDeleteDialog,
    closeMenu: closeDeleteDialog,
  } = useOpenMenu();

  const deleteExternalConnection = useDeleteExternalConnection();
  const onDelete = () => deleteExternalConnection(connection.id);

  return (
    <>
      <Styled.Container>
        <Styled.Body>
          <Styled.IconContainer>
            <ExternalConnectionIcon provider={connection.provider} />
          </Styled.IconContainer>
          <Styled.Email>{connection.providerInfo.email}</Styled.Email>
          <IconButton
            size={IconButtonSizes.Small}
            color={IconButtonColors.Default}
            icon={Trash2}
            onClick={openDeleteDialog}
          />
        </Styled.Body>

        <Suspense fallback={<LoadingCalendarConnectionList />}>
          <CalendarConnectionList
            connectionId={connection.id}
            blacklist={connection.settings?.calendarBlacklist}
          />
        </Suspense>
      </Styled.Container>

      {deleteDialogOpen && (
        <Dialog open onClose={closeDeleteDialog}>
          <Styled.DialogHeader>
            <Typography variant="h5" component="h1">
              {t(
                'forms.user-settings.sections.calendar.delete-external.dialog.title',
              )}
            </Typography>
          </Styled.DialogHeader>

          <Styled.DialogBody>
            <Typography variant="body2">
              {t(
                'forms.user-settings.sections.calendar.delete-external.dialog.description',
              )}
            </Typography>
          </Styled.DialogBody>

          <Styled.DialogFooter>
            <Button
              variant={Variants.Outlined}
              size={Sizes.Medium}
              color={Colors.Inherit}
              onClick={closeDeleteDialog}
            >
              {t(
                'forms.user-settings.sections.calendar.delete-external.dialog.buttons.cancel.label',
              )}
            </Button>
            <Button size={Sizes.Medium} color={Colors.Error} onClick={onDelete}>
              {t(
                'forms.user-settings.sections.calendar.delete-external.dialog.buttons.submit.label',
              )}
            </Button>
          </Styled.DialogFooter>
        </Dialog>
      )}
    </>
  );
};
